import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useWebSocket } from '../hooks/useWebSocket';
import { getApiUrl } from '../utils/api';
import PlayerList from '../components/PlayerList';
import { QRCodeSVG } from 'qrcode.react';
import { playAnnouncement, cancelAnnouncement, getVoiceMode, setVoiceMode, VOICE_MODES } from '../utils/audioAnnouncements';
import './AdminRoom.css';

// Phase descriptions for TTS
const PHASE_DESCRIPTIONS = {
  'werewolf': 'Màn đêm buông xuống, tất cả ngủ đi. Sói ơi dậy đi, hãy chọn nạn nhân của đêm nay.',
  'doctor': 'Bảo Vệ ơi dậy đi, hãy chọn một người để cứu.',
  'seer': 'Tiên Tri ơi dậy đi, hãy chọn một người mà tiên tri muốn soi xem có phải là sói không.',
  'witch-save': 'Phù Thuỷ ơi dậy đi, đêm nay người này bị giết, phù thuỷ có muốn cứu không?',
  'witch-kill': 'Đêm nay phù thuỷ có muốn giết ai không?',
  'hunter': 'Vì thợ săn đã bị giết, hãy chọn 1 người chơi chết cùng',
  'hunter-aim': 'Thợ săn ơi dậy đi, hãy chọn 1 người chơi để nhắm. Nếu bạn bị sói giết đêm nay, người đó sẽ chết theo.',
  'voting-accuse': 'Hãy bỏ phiếu để treo cổ một người mà bạn nghĩ là sói.',
  'defense': 'Người bị nghi ngờ đang bảo vệ bản thân.',
  'voting-decision': 'Hãy quyết định: Giết người bị nghi ngờ hay đổi ý?',
  'discussion': 'Hãy thảo luận để tìm ra sói.'
};

// Helper function to determine message key for vote tie announcements
function getVoteTieMessageKey(data) {
  if (data.phase === 'voting-decision') {
    return 'vote_tie_decision';
  } else if (data.phase === 'voting-accuse') {
    if (data.requiresRevote && data.round >= 4) {
      return 'vote_tie_accuse_round_4_plus';
    } else {
      // Check if it's insufficient votes or tie
      const message = data.message || data.displayMessage || '';
      if (message.includes('thấp hơn 1 nửa') || message.includes('thấp hơn 1 nửa')) {
        return 'vote_insufficient_rounds_1_3';
      } else {
        return 'vote_tie_accuse_rounds_1_3';
      }
    }
  }
  // Fallback
  return 'vote_tie_accuse_rounds_1_3';
}

// Helper function to determine message key for vote result announcements
function getVoteResultMessageKey(data) {
  if (data.result === 'killed') {
    return 'vote_result_killed';
  } else if (data.result === 'spared') {
    return 'vote_result_spared';
  }
  return null;
}

// Helper function to determine message key for phase announcements
function getPhaseMessageKey(phase, gameState) {
  const phaseKeyMap = {
    'werewolf': 'phase_werewolf',
    'doctor': 'phase_doctor',
    'seer': 'phase_seer',
    'witch-save': 'phase_witch_save',
    'witch-kill': 'phase_witch_kill',
    'defense': 'phase_defense',
    'voting-decision': 'phase_voting_decision',
    'discussion': null, // No audio file for discussion
    'hunter': gameState === 'night' ? 'phase_hunter_aim' : 'phase_hunter_revenge',
    'hunter-aim': 'phase_hunter_aim',
    'voting-accuse': null, // Handled dynamically based on victim count
  };
  return phaseKeyMap[phase] || null;
}

function AdminRoom() {
  const { roomId } = useParams();
  const [gameState, setGameState] = useState(null);
  const [roomInfo, setRoomInfo] = useState(null);
  const [roomLink, setRoomLink] = useState('');
  const [speechEnabled, setSpeechEnabled] = useState(false);
  const [voiceMode, setVoiceModeState] = useState(() => {
    // Load from localStorage on mount
    return getVoiceMode();
  });
  const speechUnlockedRef = useRef(false); // Track if speech is actually unlocked (test utterance started)
  const currentAudioRef = useRef(null); // Track current audio instance for cancellation
  const lastPhaseRef = useRef(null);
  const lastGameStateRef = useRef(null); // Track gameState (night/day) changes
  const lastRoundRef = useRef(null); // Track round changes to detect new day phases
  const gameFinishedAnnouncedRef = useRef(false);
  const voteResultAnnouncingRef = useRef(false);
  const lastNightVictimsRef = useRef(null);
  const isAnnouncingRef = useRef(false);
  const manualReadRef = useRef(false); // Flag to prevent duplicate when manually reading phase

  const handleMessage = (data) => {
    console.log('[AdminRoom] 📥 Received WebSocket message:', data.type, data);
    if (data.type === 'voteTie' || data.type === 'voteResult') {
      console.log('[AdminRoom] 🚨🚨🚨 ANNOUNCEMENT MESSAGE RECEIVED:', data.type, JSON.stringify(data, null, 2));
      console.log('[AdminRoom] 🚨🚨🚨 Full message object:', data);
      console.log('[AdminRoom] 🚨🚨🚨 Message phase:', data.phase, 'Message round:', data.round);
    }
    
    if (data.type === 'gameState') {
      setGameState(data.gameState);
      
      // Check if voteResult is in gameState and announce it if we haven't already
      if (data.gameState && data.gameState.voteResult && !voteResultAnnouncingRef.current) {
        const voteResult = data.gameState.voteResult;
        console.log('[AdminRoom] 🔄 Found voteResult in gameState, triggering announcement:', voteResult);
        // Trigger voteResult announcement by simulating the message
        setTimeout(() => {
          handleMessage({ type: 'voteResult', ...voteResult });
        }, 100);
      }
    } else if (data.type === 'voteTie') {
      // Handle vote tie announcement
      console.log('[AdminRoom] Received voteTie:', data);
      voteResultAnnouncingRef.current = true; // Mark that we're announcing vote tie
      
      // Use message for announcement (announcement text), displayMessage is for player screen
      const announcement = data.message || data.displayMessage || 'Vì số phiếu bằng nhau, nên không có ai bị treo cổ, trò chơi tiếp tục';
      
      console.log('[AdminRoom] Vote tie announcement:', announcement);
      console.log('[AdminRoom] Speech synthesis available:', 'speechSynthesis' in window);
      console.log('[AdminRoom] Speech enabled state:', speechEnabled);
      console.log('[AdminRoom] Speech unlocked ref:', speechUnlockedRef.current);
      
      // Try to enable speech if not already enabled (voteTie announcements should always work)
      // Browsers require user interaction to enable speech, but if speech was previously enabled,
      // we should be able to speak even without explicit user interaction for this announcement
      if ('speechSynthesis' in window && !speechEnabled && !speechUnlockedRef.current) {
        console.log('[AdminRoom] Speech not enabled - attempting to enable for voteTie');
        // Mark as enabled immediately (optimistic)
        setSpeechEnabled(true);
        speechUnlockedRef.current = true;
        // Try a silent test utterance to unlock speech - this will work if speech was previously unlocked
        // by user interaction, but will fail silently if not (which is okay - we'll try to speak anyway)
        try {
          const testUtterance = new SpeechSynthesisUtterance('');
          testUtterance.volume = 0;
          testUtterance.onstart = () => {
            console.log('[AdminRoom] ✅ Speech unlocked for voteTie');
            speechUnlockedRef.current = true;
          };
          testUtterance.onerror = (error) => {
            console.warn('[AdminRoom] ⚠️ Could not unlock speech (may require user interaction):', error);
            // Still try to speak - might work if speech was previously unlocked
          };
          window.speechSynthesis.speak(testUtterance);
          // Don't wait for test utterance - proceed immediately with announcement
        } catch (e) {
          console.warn('[AdminRoom] ⚠️ Exception trying to unlock speech:', e);
          // Still proceed - might work anyway
        }
      }
      
      // Proceed with announcement - don't require speechEnabled check for voteTie
      if (announcement) {
        console.log('[AdminRoom] ✅ Proceeding with vote tie announcement');
        
        // Cancel any ongoing announcement
        if (currentAudioRef.current) {
          cancelAnnouncement(currentAudioRef.current);
          currentAudioRef.current = null;
        }
        
        // Determine message key based on vote tie data
        const messageKey = getVoteTieMessageKey(data);
        console.log('[AdminRoom] Vote tie message key:', messageKey);
        
        // Play announcement using audio utility (handles both audio and TTS)
        const audioInstance = playAnnouncement(messageKey, announcement, {
          onstart: () => {
            console.log('[AdminRoom] ✅ Vote tie announcement started');
          },
          onend: () => {
            console.log('[AdminRoom] ✅ Vote tie announcement finished');
            voteResultAnnouncingRef.current = false;
            currentAudioRef.current = null;
            // Reset phase refs so the next phase change will trigger an announcement
            console.log('[AdminRoom] Resetting phase refs to allow next phase announcement after vote tie');
            lastPhaseRef.current = null;
            lastGameStateRef.current = null;
            lastRoundRef.current = null;
            // If requires revote, request updated state
            if (data.requiresRevote) {
              setTimeout(() => {
                send({ type: 'getState', roomId, admin: true });
              }, 500);
            } else {
              // For rounds 1-3, the game transitions to night phase immediately after vote tie
              setTimeout(() => {
                console.log('[AdminRoom] Requesting updated game state after vote tie to trigger phase announcement');
                send({ type: 'getState', roomId, admin: true });
              }, 100);
            }
          },
          onerror: (error) => {
            console.error('[AdminRoom] Vote tie announcement error:', error);
            voteResultAnnouncingRef.current = false;
            currentAudioRef.current = null;
            // Reset phase refs on error so next phase can be announced
            lastPhaseRef.current = null;
            lastGameStateRef.current = null;
            lastRoundRef.current = null;
          }
        });
        
        // Store audio instance for potential cancellation
        if (audioInstance) {
          currentAudioRef.current = audioInstance;
        }
      } else {
        // Cannot announce - log reason
        if (!announcement) {
          console.warn('[AdminRoom] No announcement generated for voteTie:', data);
        } else if (!('speechSynthesis' in window)) {
          console.warn('[AdminRoom] Speech synthesis not available');
        } else {
          console.warn('[AdminRoom] Unexpected: Cannot speak vote tie announcement');
        }
        voteResultAnnouncingRef.current = false;
      }
    } else if (data.type === 'voteResult') {
      // Handle vote result announcement
      console.log('[AdminRoom] 🎯 Processing voteResult announcement');
      console.log('[AdminRoom] Received voteResult data:', data);
      voteResultAnnouncingRef.current = true; // Mark that we're announcing vote result
      
      let announcement = '';
      if (data.result === 'killed') {
        announcement = `Vì số lượng phiếu giết cao hơn, nên nghi phạm đã bị xử tử`;
      } else if (data.result === 'spared') {
        announcement = `Vì số lượng phiếu giết thấp hơn hoặc bằng số lượng phiếu đổi ý, nên nghi phạm đã được bỏ qua`;
      }
      
      console.log('[AdminRoom] Vote result announcement:', announcement);
      console.log('[AdminRoom] Speech synthesis available:', 'speechSynthesis' in window);
      console.log('[AdminRoom] Speech enabled state:', speechEnabled);
      console.log('[AdminRoom] Speech unlocked ref:', speechUnlockedRef.current);
      
      if (announcement && (speechEnabled || speechUnlockedRef.current)) {
        console.log('[AdminRoom] ✅ All conditions met - proceeding with vote result announcement');
        
        // Cancel any ongoing announcement
        if (currentAudioRef.current) {
          cancelAnnouncement(currentAudioRef.current);
          currentAudioRef.current = null;
        }
        
        // Determine message key based on vote result
        const messageKey = getVoteResultMessageKey(data);
        console.log('[AdminRoom] Vote result message key:', messageKey);
        
        // Play announcement using audio utility (handles both audio and TTS)
        const audioInstance = playAnnouncement(messageKey, announcement, {
          onstart: () => {
            console.log('[AdminRoom] ✅ Vote result announcement started');
          },
          onend: () => {
            console.log('[AdminRoom] ✅ Vote result announcement finished');
            voteResultAnnouncingRef.current = false;
            currentAudioRef.current = null;
            
            // Request updated state to check if game is finished or if we should announce next phase
            // This ensures voteResult is announced before game finish announcement
            setTimeout(() => {
              send({ type: 'getState', roomId, admin: true });
            }, 100);
            
            // Also reset lastPhaseRef to allow next announcement
            // If game is finished, game finish announcement will trigger (after checking voteResultAnnouncingRef is false)
            // If game continues, phase announcement will trigger
            lastPhaseRef.current = null;
            lastGameStateRef.current = null;
            lastRoundRef.current = null;
          },
          onerror: (error) => {
            console.error('[AdminRoom] Vote result announcement error:', error);
            voteResultAnnouncingRef.current = false;
            currentAudioRef.current = null;
          }
        });
        
        // Store audio instance for potential cancellation
        if (audioInstance) {
          currentAudioRef.current = audioInstance;
        }
      } else if (!announcement) {
        console.warn('[AdminRoom] No announcement generated for voteResult:', data);
        voteResultAnnouncingRef.current = false;
      } else if (!('speechSynthesis' in window)) {
        console.warn('[AdminRoom] Speech synthesis not available');
        voteResultAnnouncingRef.current = false;
      } else {
        console.warn('[AdminRoom] ❌ Cannot speak vote result - speech not enabled or unlocked. speechEnabled:', speechEnabled, 'speechUnlockedRef:', speechUnlockedRef.current);
        voteResultAnnouncingRef.current = false;
      }
    } else if (data.type === 'gameStarted') {
      console.log('Game started successfully!');
      // Reset phase tracking refs to ensure first phase announcement triggers
      lastPhaseRef.current = null;
      lastGameStateRef.current = null;
      lastRoundRef.current = null;
      console.log('[AdminRoom] Reset phase tracking refs for new game');
      // Refresh room info and get game state
      fetchRoomInfo();
      setTimeout(() => {
        send({ type: 'getState', roomId, admin: true });
      }, 500);
    } else if (data.type === 'gameReset') {
      console.log('Game reset successfully!');
      // Refresh room info and get game state
      fetchRoomInfo();
      setTimeout(() => {
        send({ type: 'getState', roomId, admin: true });
      }, 500);
    } else if (data.type === 'playerJoined' || data.type === 'playerLeft') {
      console.log('Player joined/left, refreshing room info');
      fetchRoomInfo();
      // Also request updated game state
      setTimeout(() => {
        send({ type: 'getState', roomId, admin: true });
      }, 100);
    } else if (data.type === 'error') {
      console.error('Server error:', data.message);
      alert('Lỗi: ' + data.message);
    }
  };

  const wsUrl = process.env.REACT_APP_WS_URL || (window.location.protocol === 'https:' ? 'wss://' : 'ws://') + window.location.host;
  const { send, connected, ws } = useWebSocket(wsUrl, handleMessage);
  
  // Debug WebSocket connection
  useEffect(() => {
    console.log('[AdminRoom] WebSocket connection status:', {
      connected,
      wsUrl,
      wsReadyState: ws?.readyState,
      wsReadyStateText: ws?.readyState === WebSocket.OPEN ? 'OPEN' : ws?.readyState === WebSocket.CONNECTING ? 'CONNECTING' : ws?.readyState === WebSocket.CLOSING ? 'CLOSING' : 'CLOSED'
    });
  }, [connected, wsUrl, ws]);

  const fetchRoomInfo = useCallback(async () => {
    try {
      const apiUrl = await getApiUrl();
      const response = await fetch(`${apiUrl}/api/room/${roomId}`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      setRoomInfo(data);
    } catch (error) {
      console.error('Error fetching room info:', error);
    }
  }, [roomId]);

  useEffect(() => {
    fetchRoomInfo();
  }, [fetchRoomInfo]);

  // Register admin client and request game state when connected
  useEffect(() => {
    if (connected && roomId) {
      // Register admin client immediately when connected
      console.log('[AdminRoom] Registering admin client with roomId:', roomId);
      const registerMessage = { type: 'getState', roomId, admin: true };
      console.log('[AdminRoom] Sending registration message:', registerMessage);
      const sent = send(registerMessage);
      console.log('[AdminRoom] Registration message sent:', sent);
    } else {
      console.log('[AdminRoom] Not registering yet - connected:', connected, 'roomId:', roomId);
    }
  }, [connected, roomId, send]);

  // Request game state when roomInfo is loaded
  useEffect(() => {
    if (connected && roomInfo && roomId) {
      send({ type: 'getState', roomId, admin: true });
    }
  }, [connected, roomInfo, roomId, send]);

  // Request game state and room info periodically
  useEffect(() => {
    const interval = setInterval(() => {
      fetchRoomInfo();
      if (connected && roomId) {
        // Always register as admin when requesting state
        send({ type: 'getState', roomId, admin: true });
      }
    }, 2000);
    return () => clearInterval(interval);
  }, [roomId, roomInfo, send, fetchRoomInfo, connected]);

  // Text-to-Speech: Read phase description when phase changes
  useEffect(() => {
    console.log('[AdminRoom] Phase announcement useEffect triggered. gameState:', gameState ? {
      phase: gameState.phase,
      gameState: gameState.gameState,
      hasPlayers: !!gameState.players,
      nightVictims: gameState.nightVictims
    } : 'null');
    
    if (!gameState || !gameState.phase || gameState.gameState === 'waiting' || gameState.gameState === 'finished') {
      console.log('[AdminRoom] Skipping announcement - invalid gameState or waiting/finished');
      // Update lastGameStateRef even when skipping to track state changes
      if (gameState && gameState.gameState) {
        lastGameStateRef.current = gameState.gameState;
      }
      return;
    }
    
    // Check if this is a new game (all refs are null) - if so, always announce the first phase
    const isNewGame = lastPhaseRef.current === null && lastGameStateRef.current === null && lastRoundRef.current === null;
    if (isNewGame) {
      console.log('[AdminRoom] 🎮 New game detected - will announce initial phase:', gameState.phase);
    }
    
    // Initialize lastGameStateRef if not set, BUT be careful not to initialize to 'day' if we're in voting phase
    // This could be a missed night-to-day transition
    if (lastGameStateRef.current === null && gameState.gameState) {
      // If we're in voting phase and have victims, this might be a transition from night
      // Don't initialize to 'day' yet - let the transition detection handle it
      if (gameState.phase === 'voting-accuse' && 
          gameState.nightVictims && gameState.nightVictims.length > 0) {
        console.log('[AdminRoom] In voting phase with victims but lastGameStateRef is null - might be night-to-day transition, keeping as null for now');
        // Keep it as null so we can detect the transition
      } else {
        lastGameStateRef.current = gameState.gameState;
        console.log('[AdminRoom] Initializing lastGameStateRef to:', gameState.gameState);
      }
    }
    
    // Initialize lastRoundRef if not set
    if (lastRoundRef.current === null && gameState.round !== undefined && gameState.round !== null) {
      lastRoundRef.current = gameState.round;
      console.log('[AdminRoom] Initializing lastRoundRef to:', gameState.round);
    }

    const currentPhase = gameState.phase;
    const phaseKey = `${gameState.gameState}-${currentPhase}`;
    
    // Check if speech was just enabled and we haven't announced this phase yet
    // This handles the case where speech is enabled mid-game
    // More aggressive: if speech is enabled and phase hasn't been announced, always announce
    const speechJustEnabled = speechEnabled && 
      lastPhaseRef.current !== phaseKey &&
      gameState.gameState !== 'waiting' && 
      gameState.gameState !== 'finished';
    
    if (speechJustEnabled) {
      console.log('[AdminRoom] 🔊 Speech enabled - will announce current phase:', gameState.phase);
      console.log('[AdminRoom] speechJustEnabled check:', {
        speechEnabled,
        lastPhaseRef: lastPhaseRef.current,
        phaseKey,
        gameState: gameState.gameState
      });
    }
    
    // For voting-accuse phase, also check if nightVictims has changed
    const currentNightVictims = JSON.stringify(gameState.nightVictims || []);
    const nightVictimsChanged = lastNightVictimsRef.current !== currentNightVictims;
    
    // Check if phase has changed
    const phaseChanged = lastPhaseRef.current !== phaseKey;
    
    // Cancel any ongoing announcement when phase changes
    // BUT: Don't cancel if we're currently announcing vote result (voteResultAnnouncingRef is true)
    // This ensures vote result announcements (like 'spared') can complete before phase announcement
    if (phaseChanged && 'speechSynthesis' in window && !voteResultAnnouncingRef.current) {
      try {
        window.speechSynthesis.cancel();
      } catch (e) {
        // Ignore cancel errors
        console.log('[AdminRoom] Ignoring cancel error:', e);
      }
      isAnnouncingRef.current = false;
      console.log('[AdminRoom] Cancelled ongoing announcement due to phase change (voteResultAnnouncingRef:', voteResultAnnouncingRef.current, ')');
    } else if (phaseChanged && voteResultAnnouncingRef.current) {
      console.log('[AdminRoom] Phase changed but voteResult announcement in progress - not canceling speech');
    }
    
    // Special case: If gameState changed from 'night' to 'day', this is a transition to day phase
    // This handles auto-skip scenarios where phase might change quickly (e.g., witch auto-skipped)
    const nightToDayTransition = lastGameStateRef.current === 'night' && gameState.gameState === 'day' && 
      currentPhase === 'voting-accuse';
    
    console.log('[AdminRoom] Night-to-day transition check:', {
      lastGameState: lastGameStateRef.current,
      currentGameState: gameState.gameState,
      currentPhase,
      nightToDayTransition
    });
    
    // Check if round changed - this is a strong indicator of a new day phase
    // Check if we're in a different round than what we last tracked
    const roundChanged = gameState.round !== undefined && gameState.round !== null && 
      (lastRoundRef.current === null || lastRoundRef.current !== gameState.round);
    
    // For voting-accuse/voting phase, only announce ONCE when phase FIRST changes
    // Do NOT re-announce on vote updates (gameState changes but phase stays the same)
    if (currentPhase === 'voting-accuse') {
      // Check if we have victims data
      const hasVictims = gameState.nightVictims && gameState.nightVictims.length > 0;
      const hasVictim = gameState.nightVictim !== null && gameState.nightVictim !== undefined;
      
      // If phase changed, always announce (even without victims data yet)
      // This ensures the voting-accuse phase is announced when it first appears
      if (phaseChanged) {
        console.log('[AdminRoom] In voting phase and phase changed - will announce (phaseChanged:', phaseChanged, ')');
        // Continue to announcement below
      } else if (!hasVictims && !hasVictim) {
        // Phase hasn't changed and no victims yet - wait for victims or phase change
        console.log('[AdminRoom] In voting phase but no victims data yet and phase unchanged, skipping announcement until victims are populated or phase changes');
        // DON'T update any refs yet - we want to detect the transition when victims arrive
        // Keep lastGameStateRef unchanged so we can detect transition when victims are populated
        // Keep lastNightVictimsRef as null so we know victims weren't set yet
        // Keep lastPhaseRef unchanged so phaseChanged will be true when victims arrive
        return; // Exit early, wait for next update with victims data
      } else {
        // Phase hasn't changed but we have victims - check if we should announce
        // Don't announce if we've already announced for this combo AND round hasn't changed (this is just a vote update)
        const hasAnnouncedForThisCombo = lastPhaseRef.current === phaseKey && 
          lastNightVictimsRef.current === currentNightVictims;
        
        // If round changed, always announce (new round = new day phase)
        if (roundChanged) {
          console.log('[AdminRoom] In voting phase with victims and round changed - will announce (roundChanged:', roundChanged, ')');
        } else if (!nightVictimsChanged && hasAnnouncedForThisCombo) {
          console.log('[AdminRoom] In voting phase but phase and victims haven\'t changed and already announced and round unchanged - skipping announcement (vote update)');
          return; // Exit early - don't announce on vote updates
        } else {
          // We have victims and either victims changed or we haven't announced yet - will announce below
          console.log('[AdminRoom] In voting phase with victims - will announce (nightVictimsChanged:', nightVictimsChanged, 'hasAnnouncedForThisCombo:', hasAnnouncedForThisCombo, ')');
        }
      }
    }
    
    // Only announce if phase changed (for all phases) OR if night-to-day transition OR if nightVictims changed in voting phase after phase change
    // Note: For nightToDayTransition, refs are already updated above, so on next render it will be false
    // But we still want to announce this one time, so include it in shouldAnnounce
    // Also check if victims were just populated after a night-to-day transition (nightVictimsChanged and lastNightVictimsRef is null)
    // Also check if we missed a night-to-day transition (lastGameStateRef is 'night' but gameState is 'day')
    // Also check if we missed a night-to-day transition (lastGameStateRef is 'night' but gameState is 'day')
    // OR if lastGameStateRef is null and we're in day phase with victims (transition happened before we could track it)
    // For voting phase, check if we should announce even if phase didn't change
    // This handles cases where the transition happened too fast
    const missedNightToDayTransition = 
      currentPhase === 'voting-accuse' &&
      gameState.gameState === 'day' &&
      !nightToDayTransition && // Only if we didn't already detect it above
      (
        (lastGameStateRef.current === 'night') || // Standard missed transition
        (lastGameStateRef.current === null) // Transition happened before we could track it (don't require phaseChanged)
      );
    
    const victimsJustPopulated = currentPhase === 'voting-accuse' && 
      nightVictimsChanged && 
      lastNightVictimsRef.current === null &&
      gameState.nightVictims && 
      gameState.nightVictims.length > 0;
    
    // Also check: if we're in voting phase with victims and haven't announced for this phase/victims combo
    // This is a catch-all for cases where the transition happened too fast
    const isDayPhaseWithVictims = currentPhase === 'voting-accuse' &&
      gameState.gameState === 'day' &&
      gameState.nightVictims && gameState.nightVictims.length > 0;
    
    // Check if we've already announced for this phase/victims/round combo
    // (roundChanged is already calculated above)
    // If round changed, we definitely haven't announced for this round yet
    const alreadyAnnouncedForThisPhase = !roundChanged && 
      lastPhaseRef.current === phaseKey && 
      lastNightVictimsRef.current === currentNightVictims;
    
    // More aggressive check: if we're in day phase with victims and haven't announced, announce
    // (nightToDayTransition is already calculated above)
    // This handles the case where the transition happened so fast that lastGameStateRef was never 'night'
    // OR if lastGameStateRef is null (transition happened before we could track it)
    // OR if phase changed (new phase, should announce)
    // OR if round changed (new round, definitely should announce - this is the key fix)
    // IMPORTANT: If round changed, always announce regardless of other conditions
    const shouldAnnounceDayPhase = isDayPhaseWithVictims && 
      (roundChanged || // Round changed = always announce (highest priority)
       nightToDayTransition || // Night-to-day transition = always announce
       (!alreadyAnnouncedForThisPhase && // Haven't announced for this combo
        (lastGameStateRef.current === 'night' || 
         lastGameStateRef.current === null || 
         phaseChanged)));
    
    // For new games, always announce the first phase
    let shouldAnnounce = isNewGame || speechJustEnabled || phaseChanged || nightToDayTransition || victimsJustPopulated || missedNightToDayTransition || shouldAnnounceDayPhase;
    
    // Don't announce phase change if we're currently announcing vote result (voteTie or voteResult)
    // Wait for the vote result announcement to finish before announcing the new phase
    // This ensures voteTie announcement completes before night phase announcement
    if (voteResultAnnouncingRef.current) {
      console.log('[AdminRoom] ⏸️ Skipping phase announcement - vote result/voteTie announcement in progress. Will announce after it finishes.', {
        voteResultAnnouncing: voteResultAnnouncingRef.current,
        phaseChanged,
        phaseKey,
        lastPhase: lastPhaseRef.current
      });
      // Don't update refs yet - wait for voteTie to finish and reset them
      return;
    }
    
    console.log('[AdminRoom] Announcement check:', {
      shouldAnnounce,
      isNewGame,
      speechJustEnabled,
      phaseChanged,
      nightToDayTransition,
      missedNightToDayTransition,
      victimsJustPopulated,
      shouldAnnounceDayPhase,
      isDayPhaseWithVictims,
      alreadyAnnouncedForThisPhase,
      roundChanged,
      currentRound: gameState.round,
      lastRound: lastRoundRef.current,
      lastPhaseRef: lastPhaseRef.current,
      phaseKey,
      nightVictimsChanged,
      currentNightVictims,
      lastNightVictims: lastNightVictimsRef.current,
      currentPhase,
      gameStateValue: gameState.gameState,
      lastGameState: lastGameStateRef.current,
      nightVictims: gameState.nightVictims,
      nightVictim: gameState.nightVictim,
      nightVictimsLength: gameState.nightVictims ? gameState.nightVictims.length : 0,
      voteResultAnnouncing: voteResultAnnouncingRef.current
    });
    
    if (shouldAnnounce) {
      console.log('[AdminRoom] ✅ Should announce - proceeding with announcement');
      // Mark that we're announcing to prevent duplicates
      isAnnouncingRef.current = true;
      
      // Update refs BEFORE announcing to prevent re-triggering
      if (phaseChanged || nightToDayTransition || missedNightToDayTransition || shouldAnnounceDayPhase) {
        console.log('[AdminRoom] Updating refs - phaseChanged:', phaseChanged, 'nightToDayTransition:', nightToDayTransition, 'missedNightToDayTransition:', missedNightToDayTransition, 'shouldAnnounceDayPhase:', shouldAnnounceDayPhase);
        lastPhaseRef.current = phaseKey;
        lastGameStateRef.current = gameState.gameState;
        // Update round ref to track round changes
        if (gameState.round !== undefined && gameState.round !== null) {
          lastRoundRef.current = gameState.round;
        }
        // Also update nightVictims ref when phase changes to voting (so we don't re-announce on vote updates)
        if (currentPhase === 'voting-accuse') {
          lastNightVictimsRef.current = currentNightVictims;
        }
      } else {
        // If neither phaseChanged nor nightToDayTransition, but shouldAnnounce is true,
        // it means nightVictims changed - update that ref
        if (currentPhase === 'voting-accuse' && nightVictimsChanged) {
          lastNightVictimsRef.current = currentNightVictims;
          // Also update lastGameStateRef if it's still 'night' but gameState is 'day'
          if (lastGameStateRef.current === 'night' && gameState.gameState === 'day') {
            lastGameStateRef.current = gameState.gameState;
          }
        }
      }
      
      let description = PHASE_DESCRIPTIONS[currentPhase];
      console.log('[AdminRoom] Base description from PHASE_DESCRIPTIONS:', description, 'for phase:', currentPhase);
      
      // If no description found for this phase, log warning
      if (!description) {
        console.warn('[AdminRoom] No description found for phase:', currentPhase, 'Available phases:', Object.keys(PHASE_DESCRIPTIONS));
      }
      
      // For hunter phase, check if it's night or day phase
      if (currentPhase === 'hunter') {
        if (gameState.gameState === 'night') {
          // Night phase: Hunter aims at target
          description = PHASE_DESCRIPTIONS['hunter-aim'];
        } else {
          // Day phase: Hunter was killed by vote, chooses revenge target
          description = PHASE_DESCRIPTIONS['hunter']; // 'Vì thợ săn đã bị giết, hãy chọn 1 người chơi chết cùng'
        }
      }
      
      // For voting-accuse phase (day phase), include all victims if someone died
      if (currentPhase === 'voting-accuse') {
        // Use nightVictims array if available, otherwise fall back to nightVictim for backward compatibility
        // Filter out null/undefined/empty string values to get accurate count
        // IMPORTANT: Trust the server's nightVictim value - if it's null, no one died this night
        // Do NOT use a fallback that counts all dead players, as that includes players from previous rounds
        let victims = [];
        if (gameState.nightVictims && Array.isArray(gameState.nightVictims) && gameState.nightVictims.length > 0) {
          victims = gameState.nightVictims.filter(v => v != null && v !== undefined && v !== '');
        } else if (gameState.nightVictim != null && gameState.nightVictim !== undefined && gameState.nightVictim !== '') {
          victims = [gameState.nightVictim];
        }
        // If nightVictim is null/undefined/empty, victims array will be empty, meaning no one died
        
        console.log('[AdminRoom] 🔍 Day phase announcement check:');
        console.log('[AdminRoom]   - nightVictims:', gameState.nightVictims, '(type:', typeof gameState.nightVictims, ', isArray:', Array.isArray(gameState.nightVictims), ', length:', gameState.nightVictims?.length);
        console.log('[AdminRoom]   - nightVictim:', gameState.nightVictim, '(type:', typeof gameState.nightVictim, ')');
        console.log('[AdminRoom]   - dayVictim:', gameState.dayVictim);
        console.log('[AdminRoom]   - victims array after filtering:', victims, 'count:', victims.length);
        console.log('[AdminRoom]   - Raw gameState keys:', Object.keys(gameState).filter(k => k.includes('victim')));
        
        // Debug: Check if any players are dead (for troubleshooting only - not used for announcement)
        if (gameState.players) {
          const deadPlayers = gameState.players.filter(p => !p.isAlive);
          console.log('[AdminRoom]   - Dead players (for debugging only):', deadPlayers.map(p => ({ id: p.id, name: p.name })));
        }
        
        if (victims.length >= 1) {
            description = `Mọi người mở mắt ra. Đêm qua, có ${victims.length} người đã bị giết. Hãy bỏ phiếu để treo cổ một người mà bạn nghĩ là sói!`;
        } else {
          description = 'Mọi người mở mắt ra. Đêm qua không ai chết. Hãy bỏ phiếu để treo cổ một người mà bạn nghĩ là sói!';
        }
      }
      
      console.log('[AdminRoom] Final description before speaking:', description);
      
      // If still no description, use a fallback
      if (!description) {
        console.warn('[AdminRoom] No description available for phase:', currentPhase, '- using fallback');
        description = `Giai đoạn: ${currentPhase}`;
      }
      
      if (description && (speechEnabled || speechUnlockedRef.current)) {
        console.log('[AdminRoom] ✅ Proceeding with phase announcement');
        console.log('[AdminRoom] Speech enabled state:', speechEnabled);
        console.log('[AdminRoom] Speech unlocked ref:', speechUnlockedRef.current);
        
        // Cancel any ongoing announcement before starting new one
        if (currentAudioRef.current) {
          cancelAnnouncement(currentAudioRef.current);
          currentAudioRef.current = null;
        }
        // Reset the announcing flag since we're cancelling
        isAnnouncingRef.current = false;
        
        // Determine message key based on phase
        let messageKey = getPhaseMessageKey(currentPhase, gameState.gameState);
        
        // For voting-accuse phase, determine message key based on victim count
        if (currentPhase === 'voting-accuse') {
          let victims = [];
          if (gameState.nightVictims && Array.isArray(gameState.nightVictims) && gameState.nightVictims.length > 0) {
            victims = gameState.nightVictims.filter(v => v != null && v !== undefined && v !== '');
          } else if (gameState.nightVictim != null && gameState.nightVictim !== undefined && gameState.nightVictim !== '') {
            victims = [gameState.nightVictim];
          }
          
          if (victims.length === 0) {
            messageKey = 'phase_voting_accuse_0_deaths';
          } else if (victims.length === 1) {
            messageKey = 'phase_voting_accuse_1_death';
          } else if (victims.length === 2) {
            messageKey = 'phase_voting_accuse_2_deaths';
          } else if (victims.length === 3) {
            messageKey = 'phase_voting_accuse_3_deaths';
          } else {
            // For 4+ deaths, use TTS fallback (no audio file for N deaths)
            messageKey = null;
          }
        }
        
        console.log('[AdminRoom] Phase message key:', messageKey, 'for phase:', currentPhase);
        
        // If no message key (e.g., discussion phase or 4+ deaths), use TTS only
        if (!messageKey) {
          console.log('[AdminRoom] No audio file for this phase, using TTS fallback');
          // Use TTS directly for phases without audio files
          const audioInstance = playAnnouncement('', description, { fallbackToTTS: true });
          if (audioInstance) {
            currentAudioRef.current = audioInstance;
          }
        } else {
          // Play announcement using audio utility (handles both audio and TTS)
          const audioInstance = playAnnouncement(messageKey, description, {
            onstart: () => {
              console.log('[AdminRoom] Phase announcement started');
            },
            onend: () => {
              console.log('[AdminRoom] Phase announcement finished');
              isAnnouncingRef.current = false;
              currentAudioRef.current = null;
            },
            onerror: (error) => {
              // Handle 'not-allowed' error gracefully - this is a browser permission issue
              if (error && error.error === 'not-allowed') {
                console.warn('[AdminRoom] Speech synthesis blocked by browser (requires user interaction). Announcements will be silent.');
              } else {
                console.error('[AdminRoom] Phase announcement error:', error);
              }
              isAnnouncingRef.current = false;
              currentAudioRef.current = null;
            }
          });
          
          // Store audio instance for potential cancellation
          if (audioInstance) {
            currentAudioRef.current = audioInstance;
          }
        }
      } else {
        // No description to speak, or speech not enabled/unlocked, reset flag
        if (!description) {
          console.warn('[AdminRoom] ❌ Cannot speak - no description');
        } else if (!('speechSynthesis' in window)) {
          console.warn('[AdminRoom] ❌ Cannot speak - speechSynthesis not available');
        } else if (!speechEnabled && !speechUnlockedRef.current) {
          console.warn('[AdminRoom] ❌ Cannot speak - speech not enabled or unlocked. speechEnabled:', speechEnabled, 'speechUnlockedRef:', speechUnlockedRef.current);
        }
        isAnnouncingRef.current = false;
      }
    }
    
    // Always update lastGameStateRef at the end to track state changes for next render
    // This ensures we can detect night-to-day transitions even if we skip announcements
    // Only update if it hasn't been updated already during the announcement logic above
    if (gameState && gameState.gameState) {
      // Check if it was already updated in the announcement block
      const wasUpdatedInAnnouncement = (phaseChanged || nightToDayTransition) && lastGameStateRef.current === gameState.gameState;
      if (!wasUpdatedInAnnouncement && lastGameStateRef.current !== gameState.gameState) {
        console.log('[AdminRoom] Updating lastGameStateRef from', lastGameStateRef.current, 'to', gameState.gameState, '(not updated in announcement block)');
        lastGameStateRef.current = gameState.gameState;
      }
    }
    
    // Depend on gameState but use refs to prevent duplicate announcements
    // The refs (lastPhaseRef, lastNightVictimsRef, lastGameStateRef) ensure we only announce when phase/victims/state actually change
  }, [gameState, speechEnabled]);

  // Force announcement when speech is enabled
  useEffect(() => {
    // Only run when speechEnabled changes to true, not on every gameState change
    if (speechEnabled && gameState && gameState.phase && gameState.gameState !== 'waiting' && gameState.gameState !== 'finished') {
      console.log('[AdminRoom] 🔊 Speech enabled useEffect triggered');
      console.log('[AdminRoom] Current phase:', gameState.phase, 'lastPhaseRef:', lastPhaseRef.current);
      
      const phaseKey = `${gameState.gameState}-${gameState.phase}`;
      const shouldForceAnnounce = lastPhaseRef.current !== phaseKey;
      
      if (shouldForceAnnounce) {
        console.log('[AdminRoom] ✅ Speech enabled and phase not announced yet - resetting refs to force announcement');
        // Reset refs to force announcement - this will make speechJustEnabled true in the phase announcement useEffect
        lastPhaseRef.current = null;
        lastGameStateRef.current = null;
        lastRoundRef.current = null;
        // Force a re-render by creating a new gameState object
        // This will trigger the phase announcement useEffect
        setGameState(prev => prev ? { ...prev, _forceUpdate: Date.now() } : null);
      } else {
        console.log('[AdminRoom] Phase already announced (lastPhaseRef matches phaseKey), skipping');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [speechEnabled]);

  // Load voices when component mounts (some browsers need this)
  useEffect(() => {
    if ('speechSynthesis' in window) {
      // Some browsers need voices to be loaded
      const loadVoices = () => {
        window.speechSynthesis.getVoices();
      };
      loadVoices();
      if (window.speechSynthesis.onvoiceschanged !== undefined) {
        window.speechSynthesis.onvoiceschanged = loadVoices;
      }
    }
  }, []);

  // Text-to-Speech: Announce game end
  useEffect(() => {
    if (!gameState || gameState.gameState !== 'finished' || !gameState.winner) {
      // Reset announcement flag when game is not finished
      if (gameState && gameState.gameState !== 'finished') {
        gameFinishedAnnouncedRef.current = false;
      }
      return;
    }

    // Only announce once when game finishes
    // BUT: Wait for voteResult announcement to complete first
    // This ensures voteResult is announced before game finish announcement
    if (!gameFinishedAnnouncedRef.current) {
      // Check if voteResult is currently being announced - if so, wait for it to complete
      if (voteResultAnnouncingRef.current) {
        console.log('[AdminRoom] ⏸️ Game finished but voteResult announcement in progress - will announce game finish after voteResult completes');
        // Don't announce yet - the voteResult onend callback will request gameState update
        // which will trigger this useEffect again, and then voteResultAnnouncingRef will be false
        return;
      }
      
      // Delay announcement to ensure voteResult has time to complete (if it was just finishing)
      // Also gives time for voteResult message to be processed if it arrives slightly after gameState
      setTimeout(() => {
        // Double-check that voteResult is not announcing (might have started during delay)
        if (voteResultAnnouncingRef.current) {
          console.log('[AdminRoom] ⏸️ VoteResult announcement started during delay - will wait for it to complete');
          return;
        }
        
        // Check if we've already announced (might have been triggered by another state update)
        if (gameFinishedAnnouncedRef.current) {
          return; // Already announced
        }
        
        gameFinishedAnnouncedRef.current = true;
        
        // Translate winner to Vietnamese
        const winnerText = gameState.winner === 'werewolves' ? 'Ma Sói' : 'Dân Làng';
        const announcement = `Trò chơi Kết Thúc, ${winnerText} Thắng!`;
        
        // Cancel any ongoing announcement
        if (currentAudioRef.current) {
          cancelAnnouncement(currentAudioRef.current);
          currentAudioRef.current = null;
        }
        
        // Determine message key based on winner
        const messageKey = gameState.winner === 'werewolves' ? 'winner_werewolf' : 'winner_villager';
        console.log('[AdminRoom] Game finish message key:', messageKey);
        
        // Play announcement using audio utility (handles both audio and TTS)
        const audioInstance = playAnnouncement(messageKey, announcement, {
          onerror: (error) => {
            // Handle 'not-allowed' error gracefully - this is a browser permission issue
            if (error && error.error === 'not-allowed') {
              console.warn('[AdminRoom] Speech synthesis blocked by browser (requires user interaction). Game end announcement will be silent.');
            } else {
              console.error('[AdminRoom] Game finish announcement error:', error);
            }
            gameFinishedAnnouncedRef.current = false;
            currentAudioRef.current = null;
          }
        });
        
        // Store audio instance for potential cancellation
        if (audioInstance) {
          currentAudioRef.current = audioInstance;
        }
      }, 1000); // Delay to ensure voteResult announcement has time to complete first
    }
  }, [gameState, speechEnabled]);

  // Helper function to automatically enable speech when any button is clicked
  const tryEnableSpeech = useCallback(() => {
    if (!speechEnabled && 'speechSynthesis' in window) {
      console.log('[AdminRoom] 🔄 Auto-enabling speech from user interaction');
      // Set speechEnabled immediately since user interaction just happened
      // This is the key - we enable it right away, then test to confirm
      setSpeechEnabled(true);
      speechUnlockedRef.current = true; // Mark as unlocked immediately
      
      // Reset refs to force announcement of current phase when speech is enabled
      // This ensures that when speech is enabled, the current phase will be announced
      console.log('[AdminRoom] Resetting refs to force announcement when speech is enabled');
      lastPhaseRef.current = null;
      lastGameStateRef.current = null;
      lastRoundRef.current = null;
      
      try {
        // Use a very short, silent utterance to unlock speech
        // The key is to let it actually start speaking (not cancel immediately)
        const testUtterance = new SpeechSynthesisUtterance('');
        testUtterance.lang = 'vi-VN';
        testUtterance.volume = 0; // Silent
        testUtterance.rate = 10; // Very fast
        testUtterance.pitch = 1;
        
        testUtterance.onstart = () => {
          console.log('[AdminRoom] ✅ Speech test utterance started - speech is unlocked');
          speechUnlockedRef.current = true; // Mark speech as actually unlocked
          console.log('[AdminRoom] ✅ Speech unlocked ref set to true');
        };
        
        testUtterance.onend = () => {
          console.log('[AdminRoom] ✅ Speech auto-enabled from user interaction (onend)');
        };
        
        testUtterance.onerror = (error) => {
          // Ignore cancel/canceled errors - they're expected
          if (error.error === 'cancel' || error.error === 'canceled') {
            console.log('[AdminRoom] Test utterance canceled (expected)');
            return;
          }
          if (error.error === 'not-allowed') {
            console.warn('[AdminRoom] ❌ Speech not allowed - but we already enabled it, will try anyway');
            // Don't disable - keep it enabled and let announcements try
          } else {
            console.warn('[AdminRoom] Could not test speech:', error);
          }
        };
        
        // Call speak() - this should unlock speech due to user interaction (button click)
        // Mark as unlocked immediately since user interaction just happened
        speechUnlockedRef.current = true;
        window.speechSynthesis.speak(testUtterance);
        
        // DON'T cancel it - let it finish naturally
        // Once onstart fires, we confirm it's working, but we already marked it as unlocked
        
      } catch (e) {
        console.log('[AdminRoom] Error in auto-enable speech test:', e);
        // Speech is already enabled above, so continue
      }
    }
  }, [speechEnabled]);

  // Enable speech on any user interaction (click anywhere on the page)
  // This ensures speech is enabled as soon as the user interacts with the page
  useEffect(() => {
    if (!speechEnabled && 'speechSynthesis' in window) {
      const handleClick = () => {
        console.log('[AdminRoom] User interaction detected - enabling speech');
        tryEnableSpeech();
      };
      
      // Listen for any click on the page (capture phase to catch early)
      // Keep listening until speech is enabled (no 'once: true')
      document.addEventListener('click', handleClick, { capture: true });
      
      return () => {
        document.removeEventListener('click', handleClick, { capture: true });
      };
    }
  }, [speechEnabled, tryEnableSpeech]);

  const handleStartGame = () => {
    tryEnableSpeech(); // Auto-enable speech on button click
    // Reset refs when starting a new game to ensure first phase is announced
    console.log('[AdminRoom] Starting new game - resetting announcement refs');
    lastPhaseRef.current = null;
    lastGameStateRef.current = null;
    lastRoundRef.current = null;
    gameFinishedAnnouncedRef.current = false;
    console.log('Attempting to start game for room:', roomId);
    console.log('Current players:', roomInfo.numPlayers, 'Required:', roomInfo.config.numPlayers);
    console.log('WebSocket connected:', connected);
    
    if (!connected) {
      alert('Không thể kết nối đến server. Vui lòng kiểm tra kết nối WebSocket.');
      return;
    }
    
    if (roomInfo.numPlayers !== roomInfo.config.numPlayers) {
      alert(`Cần ${roomInfo.config.numPlayers} người chơi để bắt đầu. Hiện tại có ${roomInfo.numPlayers} người chơi.`);
      return;
    }
    
    send({
      type: 'startGame',
      roomId
    });
  };

  const handleResetGame = () => {
    tryEnableSpeech(); // Auto-enable speech on button click
    // Reset refs when resetting game to ensure first phase is announced
    console.log('[AdminRoom] Resetting game - resetting announcement refs');
    lastPhaseRef.current = null;
    lastGameStateRef.current = null;
    lastRoundRef.current = null;
    gameFinishedAnnouncedRef.current = false;
    if (!connected) {
      alert('Không thể kết nối đến server. Vui lòng kiểm tra kết nối WebSocket.');
      return;
    }
    
    if (!window.confirm('Bạn có chắc chắn muốn bắt đầu trò chơi mới? Tất cả tiến trình hiện tại sẽ bị xóa.')) {
      return;
    }
    
    send({
      type: 'resetGame',
      roomId
    });
  };

  // Set room link
  useEffect(() => {
    const link = `${window.location.origin}/${roomId}`;
    setRoomLink(link);
  }, [roomId]);

  const copyRoomLink = () => {
    tryEnableSpeech(); // Auto-enable speech on button click
    const linkToCopy = roomLink || `${window.location.origin}/room/${roomId}`;
    
    // Try modern clipboard API first
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(linkToCopy)
        .then(() => {
          alert('Đã sao chép link phòng!');
        })
        .catch(err => {
          console.error('Failed to copy to clipboard:', err);
          // Fallback to older method
          fallbackCopyToClipboard(linkToCopy);
        });
    } else {
      // Fallback for browsers that don't support clipboard API
      fallbackCopyToClipboard(linkToCopy);
    }
  };

  const fallbackCopyToClipboard = (text) => {
    // Create a temporary textarea element
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    textArea.style.top = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    
    try {
      const successful = document.execCommand('copy');
      if (successful) {
        alert('Đã sao chép link phòng!');
      } else {
        // If execCommand fails, show the link for manual copy
        prompt('Sao chép link này:', text);
      }
    } catch (err) {
      console.error('Fallback copy failed:', err);
      // Last resort: show prompt
      prompt('Sao chép link này:', text);
    } finally {
      document.body.removeChild(textArea);
    }
  };

  // Diagnostic function to check speech synthesis status
  const checkSpeechStatus = () => {
    const status = {
      speechSynthesisAvailable: 'speechSynthesis' in window,
      speechEnabled: speechEnabled,
      speechUnlocked: speechUnlockedRef.current,
      browser: navigator.userAgent,
      voicesAvailable: 0,
      vietnameseVoices: [],
      speaking: false,
      pending: false,
      paused: false
    };
    
    if ('speechSynthesis' in window) {
      const voices = window.speechSynthesis.getVoices();
      status.voicesAvailable = voices.length;
      status.vietnameseVoices = voices.filter(v => v.lang.startsWith('vi')).map(v => v.name);
      status.speaking = window.speechSynthesis.speaking;
      status.pending = window.speechSynthesis.pending;
      status.paused = window.speechSynthesis.paused;
    }
    
    console.log('[AdminRoom] 🔍 Speech Synthesis Diagnostic:', status);
    return status;
  };
  
  // Test speech function
  const testSpeech = () => {
    if (!('speechSynthesis' in window)) {
      alert('Speech synthesis không khả dụng trong trình duyệt này.');
      return;
    }
    
    console.log('[AdminRoom] 🧪 Testing speech synthesis...');
    setSpeechEnabled(true);
    speechUnlockedRef.current = true;
    
    const testUtterance = new SpeechSynthesisUtterance('Thử nghiệm thông báo bằng giọng nói');
    testUtterance.lang = 'vi-VN';
    testUtterance.rate = 0.9;
    testUtterance.volume = 1;
    
    testUtterance.onstart = () => {
      console.log('[AdminRoom] ✅ Test speech started successfully');
      alert('✅ Thử nghiệm thành công! Thông báo bằng giọng nói đang hoạt động.');
    };
    
    testUtterance.onerror = (error) => {
      console.error('[AdminRoom] ❌ Test speech error:', error);
      if (error.error === 'not-allowed') {
        alert('❌ Trình duyệt đã chặn thông báo bằng giọng nói. Vui lòng kiểm tra cài đặt trình duyệt.');
      } else {
        alert(`❌ Lỗi: ${error.error}. Vui lòng kiểm tra cài đặt trình duyệt.`);
      }
    };
    
    testUtterance.onend = () => {
      console.log('[AdminRoom] ✅ Test speech completed');
    };
    
    try {
      window.speechSynthesis.speak(testUtterance);
      console.log('[AdminRoom] Test utterance queued');
    } catch (error) {
      console.error('[AdminRoom] Error queuing test utterance:', error);
      alert('Lỗi khi thử nghiệm: ' + error.message);
    }
  };

  // Function to enable speech synthesis (requires user interaction)
  const enableSpeech = () => {
    // Very visible logging to confirm function is called
    console.log('========================================');
    console.log('[AdminRoom] 🔄🔊 enableSpeech FUNCTION CALLED');
    console.log('[AdminRoom] Button clicked - starting speech enablement');
    console.log('========================================');
    
    // Removed alert - too annoying, just use console logs
    
    if (!('speechSynthesis' in window)) {
      console.error('[AdminRoom] ❌ Speech synthesis not available in this browser');
      alert('Trình duyệt này không hỗ trợ thông báo bằng giọng nói.');
      return;
    }
    
    console.log('[AdminRoom] ✅ Speech synthesis API is available');
    
    if ('speechSynthesis' in window) {
      // Set speechEnabled immediately since user interaction just happened
      // This is critical - we enable it right away, then test to confirm
      console.log('[AdminRoom] Setting speechEnabled to true...');
      setSpeechEnabled(true);
      console.log('[AdminRoom] Setting speechUnlockedRef to true...');
      speechUnlockedRef.current = true; // Mark as unlocked immediately - user interaction just happened
      console.log('[AdminRoom] ✅✅✅ Speech enabled and unlocked immediately');
      console.log('[AdminRoom] speechEnabled state will be:', true);
      console.log('[AdminRoom] speechUnlockedRef.current is now:', speechUnlockedRef.current);
      
      // Reset refs to force announcement of current phase when speech is enabled
      console.log('[AdminRoom] Resetting refs to force announcement when speech is enabled');
      lastPhaseRef.current = null;
      lastGameStateRef.current = null;
      lastRoundRef.current = null;
      
      // Force a re-render by updating gameState (if available) to trigger phase announcement
      if (gameState) {
        console.log('[AdminRoom] Forcing gameState update to trigger phase announcement');
        setGameState({ ...gameState });
      }
      
      // Try to speak a very short test utterance
      // This user interaction will "unlock" speech synthesis for the session
      // Make it audible but fast so it's quick
      const testUtterance = new SpeechSynthesisUtterance('Kích hoạt');
      testUtterance.lang = 'vi-VN';
      testUtterance.volume = 0.3; // Slightly audible to verify it works
      testUtterance.rate = 3; // Very fast so it's quick
      
      const readCurrentPhase = () => {
        // After enabling, read the current phase if available
        if (gameState && gameState.phase && gameState.gameState !== 'waiting' && gameState.gameState !== 'finished') {
          console.log('[AdminRoom] Reading current phase after enabling speech:', gameState.phase);
          
          // Flag is already set at the start of enableSpeech, but ensure it's still set
          if (!manualReadRef.current) {
            manualReadRef.current = true;
            console.log('[AdminRoom] Set manualReadRef to true in readCurrentPhase');
          }
          
          let description = PHASE_DESCRIPTIONS[gameState.phase];
          
          // For hunter phase, check if it's night or day phase
          if (gameState.phase === 'hunter') {
            if (gameState.gameState === 'night') {
              // Night phase: Hunter aims at target
              description = PHASE_DESCRIPTIONS['hunter-aim'];
            } else {
              // Day phase: Hunter was killed by vote, chooses revenge target
              description = PHASE_DESCRIPTIONS['hunter']; // 'Vì thợ săn đã bị giết, hãy chọn 1 người chơi chết cùng'
            }
          }
          
          // For voting-accuse phase (day phase), include all victims if someone died
          if (gameState.phase === 'voting-accuse' && gameState.players) {
            let victims = gameState.nightVictims && gameState.nightVictims.length > 0 
              ? gameState.nightVictims 
              : (gameState.nightVictim ? [gameState.nightVictim] : []);
            
            if (victims.length >= 1) {
              description = `Mọi người mở mắt ra. Đêm qua, có ${victims.length} người đã bị giết. Hãy bỏ phiếu để treo cổ một người mà bạn nghĩ là sói!`;
            } else {
              description = 'Mọi người mở mắt ra. Đêm qua không ai chết. Hãy bỏ phiếu để treo cổ một người mà bạn nghĩ là sói!';
            }
          }
          
          if (description) {
            // Wait a bit for the test utterance to finish
            setTimeout(() => {
              const utterance = new SpeechSynthesisUtterance(description);
              utterance.lang = 'vi-VN';
              utterance.rate = 0.9;
              utterance.pitch = 1;
              utterance.volume = 1;
              
              // Try to use a Google voice if available
              const voices = window.speechSynthesis.getVoices();
              const googleVoice = voices.find(voice => 
                voice.name.toLowerCase().includes('google') && 
                (voice.lang.startsWith('vi') || voice.lang === 'vi-VN')
              );
              if (googleVoice) {
                utterance.voice = googleVoice;
              } else {
                const viVoice = voices.find(voice => voice.lang.startsWith('vi'));
                if (viVoice) {
                  utterance.voice = viVoice;
                }
              }
              
              utterance.onend = () => {
                console.log('[AdminRoom] Manual phase read finished');
                manualReadRef.current = false; // Reset flag after speaking
              };
              
              utterance.onerror = (error) => {
                // Ignore 'cancel' and 'canceled' errors - they're expected when we intentionally cancel speech
                if (error.error === 'cancel' || error.error === 'canceled') {
                  console.log('[AdminRoom] Manual phase read was canceled (expected)');
                  return;
                }
                console.error('[AdminRoom] Error in manual phase read:', error);
                manualReadRef.current = false; // Reset flag on error
              };
              
              console.log('[AdminRoom] Speaking current phase description:', description);
              window.speechSynthesis.speak(utterance);
            }, 300);
          } else {
            // No description, reset flag immediately
            manualReadRef.current = false;
          }
        }
      };
      
      let readPhaseCalled = false; // Flag to prevent calling readCurrentPhase multiple times
      
      testUtterance.onstart = () => {
        console.log('[AdminRoom] ✅ Test utterance started - speech is unlocked');
        speechUnlockedRef.current = true; // Mark speech as actually unlocked
        console.log('[AdminRoom] ✅ Speech unlocked ref set to true');
      };
      
      testUtterance.onend = () => {
        console.log('[AdminRoom] ✅ Test utterance finished - speech synthesis confirmed working');
        // Speech is already enabled above, just read phase
        if (!readPhaseCalled) {
          readPhaseCalled = true;
          readCurrentPhase();
        }
      };
      
      testUtterance.onerror = (error) => {
        console.error('[AdminRoom] Test utterance error:', error);
        // Ignore 'cancel' and 'canceled' errors - they're expected
        if (error.error === 'cancel' || error.error === 'canceled') {
          console.log('[AdminRoom] Test utterance was canceled (expected)');
          // Even if canceled, if onstart fired, speech is unlocked
          // So we can still try to read phase
          if (!readPhaseCalled) {
            readPhaseCalled = true;
            readCurrentPhase();
          }
          return;
        }
        
        if (error.error === 'not-allowed') {
          console.error('[AdminRoom] ❌ Speech not allowed by browser');
          alert('Trình duyệt vẫn chặn thông báo bằng giọng nói. Vui lòng kiểm tra cài đặt trình duyệt hoặc thử lại.');
          // Don't disable - keep it enabled and let announcements try
        } else {
          console.error('[AdminRoom] Error in test utterance:', error);
          // Speech is already enabled above, just read phase
          if (!readPhaseCalled) {
            readPhaseCalled = true;
            readCurrentPhase();
          }
        }
      };
      
      try {
        console.log('[AdminRoom] ========================================');
        console.log('[AdminRoom] 🗣️ Calling window.speechSynthesis.speak(testUtterance)');
        console.log('[AdminRoom] Test utterance text:', testUtterance.text);
        console.log('[AdminRoom] Test utterance volume:', testUtterance.volume);
        console.log('[AdminRoom] Test utterance rate:', testUtterance.rate);
        window.speechSynthesis.speak(testUtterance);
        console.log('[AdminRoom] ✅ speak() called - waiting for onstart event...');
        console.log('[AdminRoom] ========================================');
        // DON'T cancel it - let it finish naturally
        // Once onstart fires, speech is unlocked and we can use it
        // The utterance is fast and quiet, so it won't be too noticeable
        
        // Speech is already enabled above, but ensure readCurrentPhase is called
        // Use a timeout as fallback in case onend doesn't fire
        setTimeout(() => {
          if (!readPhaseCalled) {
            console.log('[AdminRoom] Fallback: calling readCurrentPhase after timeout');
            readPhaseCalled = true;
            readCurrentPhase();
          }
        }, 500); // Increased timeout to give onend a chance to fire first
      } catch (error) {
        console.error('[AdminRoom] Error calling speak():', error);
        // Even if speak() throws, try to enable speech - it might still work
        // Only show alert for critical errors
        if (error.message && error.message.includes('not allowed')) {
          alert('Trình duyệt vẫn chặn thông báo bằng giọng nói. Vui lòng kiểm tra cài đặt trình duyệt hoặc thử lại.');
        } else {
          // For other errors, try to enable anyway - speech might still work
          console.warn('[AdminRoom] Attempting to enable speech despite error');
          setSpeechEnabled(true);
        }
      }
    } else {
      alert('Trình duyệt của bạn không hỗ trợ thông báo bằng giọng nói.');
    }
  };

  if (!roomInfo) {
    return (
      <div className="admin-container">
        <div className="container">
          <p>Đang tải thông tin phòng...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-container">
      <div className="container">
        <h1>🎮 Bảng Điều Khiển Trò Chơi</h1>
        
        {!speechEnabled && (
          <div style={{
            backgroundColor: '#fff3cd',
            border: '2px solid #ffc107',
            borderRadius: '8px',
            padding: '20px',
            marginBottom: '20px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }}>
            <div style={{ flex: 1, minWidth: '250px' }}>
              <strong style={{ fontSize: '18px', color: '#856404' }}>🔊 Kích hoạt thông báo bằng giọng nói</strong>
              <p style={{ margin: '10px 0 0 0', fontSize: '15px', color: '#856404' }}>
                <strong>Quan trọng:</strong> Trình duyệt yêu cầu tương tác của người dùng trước khi cho phép đọc thông báo. 
                Vui lòng bấm nút <strong>"Kích hoạt"</strong> bên dưới để kích hoạt thông báo tự động.
                <br />
                <span style={{ fontSize: '13px', fontStyle: 'italic' }}>
                  Nếu không kích hoạt, các thông báo kết quả bỏ phiếu sẽ không được đọc.
                </span>
              </p>
            </div>
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                console.log('🔘 KÍCH HOẠT BUTTON CLICKED - onClick handler fired');
                console.log('Calling enableSpeech function...');
                try {
                  enableSpeech();
                  console.log('enableSpeech function called successfully');
                } catch (error) {
                  console.error('Error calling enableSpeech:', error);
                  alert('Error: ' + error.message);
                }
              }}
              style={{
                padding: '15px 30px',
                backgroundColor: '#ffc107',
                color: '#000',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '18px',
                fontWeight: 'bold',
                marginLeft: '15px',
                whiteSpace: 'nowrap',
                boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                transition: 'all 0.2s',
                minWidth: '150px'
              }}
              onMouseOver={(e) => {
                e.target.style.backgroundColor = '#ffb300';
                e.target.style.transform = 'scale(1.05)';
              }}
              onMouseOut={(e) => {
                e.target.style.backgroundColor = '#ffc107';
                e.target.style.transform = 'scale(1)';
              }}
            >
              🔊 Kích Hoạt
            </button>
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                checkSpeechStatus();
                testSpeech();
              }}
              style={{
                padding: '10px 20px',
                backgroundColor: '#2196f3',
                color: '#fff',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 'bold',
                marginLeft: '10px',
                whiteSpace: 'nowrap'
              }}
              title="Kiểm tra và thử nghiệm thông báo bằng giọng nói"
            >
              🧪 Kiểm Tra
            </button>
          </div>
        )}
        
        {speechEnabled && (
          <>
            <div style={{
              backgroundColor: '#e8f5e9',
              border: '1px solid #4caf50',
              borderRadius: '8px',
              padding: '10px 15px',
              marginBottom: '20px',
              fontSize: '14px',
              color: '#2e7d32',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <span>✅ Thông báo bằng giọng nói đã được kích hoạt</span>
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  checkSpeechStatus();
                }}
                style={{
                  marginLeft: '10px',
                  padding: '5px 10px',
                  backgroundColor: '#4caf50',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '12px'
                }}
              >
                🔍 Xem Trạng Thái
              </button>
            </div>
            <div style={{
              backgroundColor: '#fff3cd',
              border: '1px solid #ffc107',
              borderRadius: '8px',
              padding: '10px 15px',
              marginBottom: '20px'
            }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#856404' }}>
                🎤 Chọn Giọng Nói:
              </label>
              <select
                value={voiceMode}
                onChange={(e) => {
                  const mode = e.target.value;
                  setVoiceModeState(mode);
                  setVoiceMode(mode);
                  console.log('[AdminRoom] Voice mode changed to:', mode);
                  // Cancel any ongoing announcement when switching modes
                  if (currentAudioRef.current) {
                    cancelAnnouncement(currentAudioRef.current);
                    currentAudioRef.current = null;
                  }
                }}
                style={{
                  padding: '8px 12px',
                  fontSize: '14px',
                  borderRadius: '4px',
                  border: '1px solid #ddd',
                  backgroundColor: '#fff',
                  minWidth: '250px',
                  cursor: 'pointer'
                }}
              >
                <option value={VOICE_MODES.DEFAULT}>Mặc định (Google TTS)</option>
                <option value={VOICE_MODES.NGUYEN_NGOC_NGAN}>Nguyễn Ngọc Ngạn (Audio Files)</option>
              </select>
              <p style={{ marginTop: '8px', fontSize: '12px', color: '#856404', fontStyle: 'italic' }}>
                Chọn giọng nói để sử dụng cho tất cả các thông báo trong trò chơi.
              </p>
            </div>
          </>
        )}
        
        <div className="room-info">
          <h2>Thông Tin Phòng</h2>
          <p><strong>Mã Phòng:</strong> <span className="room-id">{roomId}</span></p>
          <p><strong>Số Người Chơi:</strong> {roomInfo.numPlayers} / {roomInfo.config.numPlayers}</p>
          <p><strong>Trạng Thái:</strong> {roomInfo.gameState === 'waiting' ? 'Đang chờ' : 'Đang chơi'}</p>
          
          <div className="room-share-section">
            <div className="qr-code-container">
              <h3>Quét mã QR để tham gia</h3>
              <div className="qr-code-wrapper">
                {roomLink ? (
                  <QRCodeSVG 
                    value={roomLink} 
                    size={200}
                    level="H"
                    includeMargin={true}
                  />
                ) : (
                  <div style={{ width: 200, height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <p>Đang tải...</p>
                  </div>
                )}
              </div>
            </div>
            <div className="room-link-section">
              <button className="button copy-button" onClick={copyRoomLink}>
                📋 Sao Chép Link Phòng
              </button>
              <p className="room-link-text">
                {roomLink || `${window.location.origin}/${roomId}`}
              </p>
            </div>
          </div>
        </div>

        <div className="admin-controls">
          <h2>Điều Khiển Trò Chơi</h2>
          
          {roomInfo.gameState === 'waiting' && (
            <div>
              <button
                className="button"
                onClick={handleStartGame}
                disabled={!connected || roomInfo.numPlayers !== roomInfo.config.numPlayers}
              >
                Bắt Đầu Trò Chơi
              </button>
              {!connected && (
                <p style={{ color: '#e74c3c', marginTop: '10px' }}>
                  ⚠️ Chưa kết nối đến server
                </p>
              )}
              {connected && roomInfo.numPlayers !== roomInfo.config.numPlayers && (
                <p style={{ color: '#e74c3c', marginTop: '10px' }}>
                  ⚠️ Cần {roomInfo.config.numPlayers} người chơi. Hiện tại có {roomInfo.numPlayers} người chơi.
                </p>
              )}
              {connected && roomInfo.numPlayers === roomInfo.config.numPlayers && (
                <p style={{ color: '#27ae60', marginTop: '10px' }}>
                  ✅ Đủ người chơi! Có thể bắt đầu trò chơi.
                </p>
              )}
            </div>
          )}

          {roomInfo.gameState !== 'waiting' && roomInfo.gameState !== 'finished' && gameState && (
            <div className="phase-control">
              <div className="current-phase">
                <h3>Giai Đoạn Hiện Tại:</h3>
                <p className="phase-name">
                  {gameState.gameState === 'night' ? '🌙 Đêm' : '☀️ Ngày'} - {getPhaseName(gameState.phase)}
                </p>
                <p style={{ color: '#666', fontSize: '14px', marginTop: '10px' }}>
                  Trò chơi sẽ tự động chuyển giai đoạn
                </p>
              </div>
            </div>
          )}

          {roomInfo.gameState === 'finished' && (
            <div className="game-finished-admin">
              <h2>Trò chơi đã kết thúc!</h2>
              <button
                className="button"
                onClick={handleResetGame}
                disabled={!connected}
                style={{ marginTop: '15px', backgroundColor: '#27ae60' }}
              >
                🎮 Bắt Đầu Trò Chơi Mới
              </button>
            </div>
          )}

          {roomInfo.gameState !== 'waiting' && roomInfo.gameState !== 'finished' && (
            <div style={{ marginTop: '15px' }}>
              <button
                className="button"
                onClick={handleResetGame}
                disabled={!connected}
                style={{ backgroundColor: '#e74c3c' }}
              >
                🔄 Bắt Đầu Trò Chơi Mới
              </button>
              <p style={{ color: '#666', fontSize: '12px', marginTop: '10px' }}>
                Cảnh báo: Hành động này sẽ xóa tiến trình hiện tại và đặt lại trò chơi về trạng thái chờ.
              </p>
            </div>
          )}
        </div>

        <div className="game-status">
          <h2>Danh Sách Người Chơi</h2>
          {gameState && gameState.players ? (
            <PlayerList 
              players={gameState.players} 
              showRole={roomInfo.gameState === 'finished'} 
              votes={gameState.votes}
              decisionVotes={gameState.decisionVotes}
              phase={gameState.phase}
              accusedPlayer={gameState.accusedPlayer}
            />
          ) : roomInfo && roomInfo.players ? (
            <PlayerList 
              players={roomInfo.players} 
              showRole={roomInfo.gameState === 'finished'} 
              votes={null}
              decisionVotes={null}
              phase={null}
              accusedPlayer={null}
            />
          ) : (
            <p>Đang tải danh sách người chơi...</p>
          )}
        </div>
      </div>
    </div>
  );
}

function getPhaseName(phase) {
  const phaseNames = {
    'werewolf': 'Ma Sói Chọn Nạn Nhân',
    'doctor': 'Bảo Vệ Chọn Người Bảo Vệ',
    'seer': 'Tiên Tri Soi',
    'witch-save': 'Phù Thuỷ Cứu',
    'witch-kill': 'Phù Thuỷ Giết',
    'discussion': 'Thảo Luận',
    'voting-accuse': 'Bỏ Phiếu Nghi Ngờ',
    'voting-decision': 'Bỏ Phiếu Quyết Định',
    'defense': 'Người Bị Buộc Tội Đang Bảo Vệ Bản Thân'
  };
  return phaseNames[phase] || phase;
}

export default AdminRoom;

